import type { MouseEvent, RefObject } from "react";
import { useRefEventOnce } from "../hooks/useRefEventOnce";
import type { ScrollOrientation } from "../hooks/useScrollBoundary";

export type ScrollIntoViewAlignment = "start" | "center" | "end";

/**
 * blocks rapid clicks restarting `scrollIntoView` to let the animation settle
 */
export const useScrollIntoViewOnce = (
  ref: RefObject<HTMLElement | null>,
  orientation: ScrollOrientation,
  align: ScrollIntoViewAlignment | undefined,
) => {
  const scrollOnce = useRefEventOnce(ref, "scrollend");

  return (event: MouseEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement;
    // nearest clickable ancestor; excludes group wrappers (e.g.
    // ToggleGroup.Root) so a background click doesn't scroll to the container
    const clicked = target.closest<HTMLElement>(
      "button, [role='button'], a, [tabindex]:not([role='group'])",
    );
    if (!clicked || !event.currentTarget.contains(clicked) || !align) return;

    scrollOnce(() => {
      const isHorizontal = orientation === "horizontal";
      clicked.scrollIntoView({
        behavior: "smooth",
        inline: isHorizontal ? align : "nearest",
        block: isHorizontal ? "nearest" : align,
      });
    });
  };
};
